import React from 'react';
import Layout from '../components/Layout';

const BASE = "/images/";

// Every step has exactly one image. Zigzag alternates: odd steps = text-left/img-right, even = img-left/text-right
const windowsSteps = [
  {
    num: 1, title: 'Step 1:',
    content: (
      <>
        <p>Ensure that your printer is switched on. Make that your printer is turned on and linked to a power source before moving on to the next step in the troubleshooting procedure.</p>
        <p>You may need to click a button on the printer to wake it up if it is in Sleep mode.</p>
      </>
    ),
    img: '1.jpg',
  },
  {
    num: 2, title: 'Step 2:',
    content: (
      <>
        <p>Restart your printer if it hasn't done so already. If the printer was already turned on, it may only need to be restarted to stop showing up as unavailable. To restart the printer, follow these steps:</p>
        <ul>
          <li>For 30 seconds, turn off and unplug your printer. This will allow your printer to restart and reconnect with your computer.</li>
          <li>Turn on your printer after plugging it in. Check to see if the printer is still showing as "offline" after a few moments.</li>
        </ul>
      </>
    ),
    img: '2.jpg',
  },
  {
    num: 3, title: 'Step 3:',
    content: (
      <>
        <ul>
          <li>Confirm that your printer is on the same network as your computer. If your printer is linked to your computer through Wi-Fi or ethernet, it may appear to be offline due to a network issue.</li>
          <li>The steps for checking your printer's network settings differ depending on the printer you have. To explore the printer's internal menus, you'll normally have to use the buttons on the printer code.</li>
          <li>If you're connecting to the printer through a USB cable, ensure it's securely plugged in. To rule out a broken cable connection, you could try an alternative USB cable.</li>
        </ul>
      </>
    ),
    img: '3.jpg',
  },
  {
    num: 4, title: 'Step 4:',
    content: (
      <>
        <p>Check to see if the printer tray is full of paper.</p>
        <p>If there is no paper available to print on, certain printers will go offline.</p>
      </>
    ),
    img: '4.jpg',
  },
  {
    num: 5, title: 'Step 5:',
    content: (
      <>
        <p>The print spooler should be restarted. To restart the service that manages your print jobs, follow these steps:</p>
        <ul>
          <li>To open the search bar, press Win+S and type services.</li>
          <li>In the search results, select Services.</li>
          <li>Scroll down to the Print Spooler service and right-click it.</li>
          <li>Press the Restart button. The printer dialogue will be restarted, giving the computer a chance to rediscover the printer.</li>
        </ul>
        <p>Check to see whether your printer is showing up online after a few minutes. Because the Print Spooler service takes a couple of minutes to restart, you'll have to wait a few minutes until the printer appears as identified by Windows.</p>
      </>
    ),
    img: '5.jpg',
  },
  {
    num: 6, title: 'Step 6:',
    content: (
      <>
        <p>From the Windows Start menu, choose <strong>Settings</strong>.</p>
        <p>There are some further troubleshooting methods you can try if the printer still appears to be down.</p>
      </>
    ),
    img: '6.jpg',
  },
  {
    num: 7, title: 'Step 7:',
    content: (
      <>
        <p>Choose your devices. It can be found in the Settings menu.</p>
      </>
    ),
    img: '7.jpg',
  },
  {
    num: 8, title: 'Step 8:',
    content: (
      <>
        <p>Select Printers &amp; Scanners from the drop-down menu.</p>
        <p>Your printer, as well as any other devices connected to your computer, should now be listed.</p>
      </>
    ),
    img: '8.jpg',
  },
  {
    num: 9, title: 'Step 9:',
    content: (
      <>
        <p>Make sure the print queue is empty. To remove all documents from the queue, follow these steps:</p>
        <ul>
          <li>Select your printer and then Open Queue from the drop-down menu.</li>
          <li>Choose a document from the queue.</li>
          <li>Under "Document," select Cancel.</li>
          <li>Rep until the line is completely empty.</li>
          <li>Examine the printer to see if it is operational. Continue with this procedure if you're still having problems.</li>
        </ul>
      </>
    ),
    img: '9.jpg',
  },
  {
    num: 10, title: 'Step 10:',
    content: (
      <>
        <p>Select Remove device from the printer's name.</p>
        <p>This step will remove the printer's drivers so you may re-add them if clearing the queue didn't work.</p>
        <p style={{ fontWeight: 700, color: 'var(--primary)', marginTop: '0.5rem' }}>Not Affiliated with Manufacturer</p>
      </>
    ),
    img: '10.jpg',
  },
  {
    num: 11, title: 'Step 11:',
    content: (
      <>
        <p>Select Add a device or printer from the drop-down menu.</p>
        <p>This option can be found at the top of the "Printers &amp; Scanners" menu.</p>
      </>
    ),
    img: '11.jpg',
  },
  {
    num: 12, title: 'Step 12:',
    content: (
      <>
        <p>To re-add your printer, follow the on-screen directions.</p>
        <p>Check to see if the printer is showing up as "online" after it has been re-added.</p>
      </>
    ),
    img: '12.jpg',
  },
  {
    num: 13, title: 'Step 13:',
    content: (
      <>
        <p>Make sure your printer is set in the default. If the printer is still not working, set it as the default printer so that Windows recognises it as the one you wish to use. Here's how to do it:</p>
        <ul>
          <li>Navigate to the Printers &amp; Scanners section of the menu.</li>
          <li>Select the printer that you want to be the default.</li>
          <li>Choose the option to open the queue.</li>
          <li>In the menu for your printer's queue, choose Set As Default Printer. This is found in the Printer menu, which is seen on the printer queue window.</li>
        </ul>
        <p>For more information, contact the manufacturer of your printer. If you're still having problems with your printer, it's most likely a hardware problem. For more information about connecting your printer to your PC, contact the printer's manufacturer.</p>
      </>
    ),
    img: '13.jpg',
  },
];

const macSteps = [
  {
    num: 1, title: 'Step 1:',
    content: (
      <>
        <p>Ensure that your printer is switched on. Make sure that your printer is turned on and linked to a power source before moving on to the next step in the troubleshooting procedure. You may need to click a button on the printer to wake it up if it is in Sleep mode.</p>
      </>
    ),
    img: '14.jpg',
  },
  {
    num: 2, title: 'Step 2:',
    content: (
      <>
        <p>Restart your printer if necessary. If the printer was already turned on, it may only need to be restarted to stop showing up as unavailable. To restart the printer, follow these steps:</p>
        <ul>
          <li>For 30 seconds, turn off and unplug your printer. This will allow your printer to restart and reconnect with your computer.</li>
          <li>Turn on your printer after plugging it in. Check to see if the printer is still showing as "offline" after a few moments.</li>
        </ul>
      </>
    ),
    img: '15.jpg',
  },
  {
    num: 3, title: 'Step 3:',
    content: (
      <>
        <p>Confirm that your printer is on the same network as your computer. If your printer is connected to your computer via Wi-Fi or ethernet, it may appear to be offline due to a network issue.</p>
        <p>The steps for checking your printer's network settings differ depending on the printer you have. To navigate the printer's internal menus, you'll usually have to use the buttons on the printer itself.</p>
        <p>If you're connecting to the printer through a USB cable, ensure it's securely plugged in. To rule out a broken cable connection, you could try an alternative USB cable.</p>
      </>
    ),
    img: '16.jpg',
  },
  {
    num: 4, title: 'Step 4:',
    content: (
      <>
        <p>Check to see if the printer tray is full of paper.</p>
        <p>If there is no paper available to print on, certain printers will go offline.</p>
      </>
    ),
    img: '17.jpg',
  },
  {
    num: 5, title: 'Step 5:',
    content: (
      <>
        <p>Select the Apple menu from the drop-down menu.</p>
        <p>It's located in the upper-left corner of the screen.</p>
      </>
    ),
    img: '18.jpg',
  },
  {
    num: 6, title: 'Step 6:',
    content: (
      <>
        <p>On the menu, select System Preferences.</p>
      </>
    ),
    img: '19.jpg',
  },
  {
    num: 7, title: 'Step 7:',
    content: (
      <>
        <p>Select Printers &amp; Scanners or Print &amp; Scan from the drop-down menu. This opens a new window with a menu listing all printers connected to your Mac. In this option, choose the printer you want to connect.</p>
      </>
    ),
    img: '20.jpg',
  },
  {
    num: 8, title: 'Step 8:',
    content: (
      <>
        <p>In the Printers pane, right-click anywhere.</p>
        <p>This brings up a little pop-up menu with more settings for the printer in the window.</p>
      </>
    ),
    img: '21.jpg',
  },
  {
    num: 9, title: 'Step 9:',
    content: (
      <>
        <p>In the menu, choose Reset printing system.</p>
        <p>This opens a new window asking you to confirm that you want to reset the printing system.</p>
      </>
    ),
    img: '22.jpg',
  },
  {
    num: 10, title: 'Step 10:',
    content: (
      <>
        <p>In the confirmation window, click Reset. This will prompt you for the Administrator's username and password.</p>
        <p>This will remove all connected printers and scanners, so make sure to re-add any devices you still want to use after restarting your Mac.</p>
      </>
    ),
    img: '23.jpg',
  },
  {
    num: 11, title: 'Step 11:',
    content: (
      <>
        <p>Click OK after entering the Administrator's name and password.</p>
        <p>Once you've done that, the system will finish resetting your Mac's printing system.</p>
      </>
    ),
    img: '24.jpg',
  },
  {
    num: 12, title: 'Step 12:',
    content: (
      <>
        <p>Return to the Printers &amp; Scanners section.</p>
        <p>You'll need to re-add the printer to your Mac after you've reset your printing system.</p>
      </>
    ),
    img: '25.jpg',
  },
  {
    num: 13, title: 'Step 13:',
    content: (
      <>
        <p>Select Add a Device from the drop-down menu. This brings up a window that searches for your printer and automatically detects it.</p>
        <p>Before continuing with this step, make sure your printer is turned on and ready to connect.</p>
      </>
    ),
    img: '26.jpg',
  },
  {
    num: 14, title: 'Step 14:',
    content: (
      <>
        <p>Click Add after selecting your printer. This will re-establish a connection between your printer and your Mac's wireless network.</p>
        <p>If there are no mechanical concerns, your printer should show online at this time. Make sure to reconnect any other devices this way to re-add them if you haven't previously.</p>
      </>
    ),
    img: '27.jpg',
  },
  {
    num: 15, title: 'Step 15:',
    content: (
      <>
        <p>Seek help from your printer's manufacturer.</p>
        <p>If you're still having problems with your printer, it's most likely a hardware problem.</p>
        <p>For more information, contact the printer's manufacturer.</p>
      </>
    ),
    img: '28.jpg',
  },
];

// reverse=false → text left, image right (odd positions)
// reverse=true  → image left, text right (even positions)
function StepBlock({ step, reverse }) {
  return (
    <div className={`zigzag-section${reverse ? ' reverse' : ''}`}>
      <div className="zigzag-text">
        <h2>{step.title}</h2>
        {step.content}
      </div>
      <div className="zigzag-media">
        <img src={`${BASE}${step.img}`} alt={`Step ${step.num} illustration`} />
      </div>
    </div>
  );
}

export default function PrinterOffline() {
  return (
    <Layout bannerTitle="Fix an Offline Printer">
      <div className="main-content" id="setup-section">

        {/* Windows Method */}
        <div className="method-header">
          <h1>Method 1 - Windows Troubleshooting</h1>
        </div>
        <div className="method-section">
          {windowsSteps.map((step, idx) => (
            <StepBlock key={step.num} step={step} reverse={idx % 2 !== 0} />
          ))}
        </div>

        {/* Mac Method */}
        <div className="method-header">
          <h1>Method 2 - Troubleshooting on Macos</h1>
        </div>
        <div className="method-section">
          {macSteps.map((step, idx) => (
            <StepBlock key={step.num} step={step} reverse={idx % 2 !== 0} />
          ))}
        </div>

      </div>
    </Layout>
  );
}
